import { shallowEqual } from "react-redux";
import React, { useCallback, useEffect, useState } from "react";

// Store
import {
    fetchPeers,
    setSelectedCountry,
    setSelectedProducts,
    setSelectedSectors,
    setPeers as setSelectedPeers,
    setDataToStore,
    fetchCategorizedSectorsThunk,
    fetchSectorsThunk,
    fetchProductsThunk,
} from "store/slice/Domain";
import { getDomain } from "store/slice/Domain";
import { getLoaderState, toggleLoader } from "store/slice/UI";
import { ListEntity, CategorizedSector } from "store/slice/Domain";
import { fetchCompanySet } from "store/slice/Project/companiesSet/companiesSetSlice";
import { getInsightById, getProjectInsight } from "store/slice/Project/insights/insightsSlice";
import { fetchProjectCompany, getProjectCompanyData } from "store/slice/Project/projectData/ProjectSlice";

// Hooks
import { unwrapResult } from "@reduxjs/toolkit";
import { useRedirectOnProjectError } from "utils/hooks";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

// Enums
import { urlPaths } from "enums/urlPaths";
import { PEERS_LIMIT } from "constants/constants";

// Services
import { columnsDomainPeerOS, fetchListOfProductsByValue, flattenSectorProductsDomain } from "services/company";

// Utils
import { differenceBy, isEmpty, isNil } from "lodash";
import {
    getFiltersElements,
    mapForChipEntity,
    locationList,
    getElementKey,
    getSelectedElements,
    getInitialPeersData,
} from "./DomainLayout.functions";
import {
    getInsightPeersPath,
    getInitialFetchResponse,
    TInitialFetch,
    getInsightFilters,
    TInitialFetchError,
} from "pages/ProjectDashboard/adapters/formattedData";

// Type
import { ChipEntity } from "types";
import { Peer } from "services/company";
import { RequestInfo } from "store/slice/store.types";
import { TAutocompleteProducts } from "./DomainLayout.types";
import { InsightData, ProjectCompanySet } from "services/projects/project.types";

const useDomainLayout = (): {
    sectors: ChipEntity[];
    products: ChipEntity[];
    company: ReturnType<typeof flattenSectorProductsDomain> | undefined;
    peers: Peer[];
    peersRequest: RequestInfo;
    domain: string | undefined;
    country: number;
    companyCountryId: number | null | undefined;
    locations: ChipEntity[];
    autoCompleteProducts: TAutocompleteProducts;
    listOfSectors: ListEntity[];
    categorizedSectors: CategorizedSector[];
    sectorsRequestStatus: RequestInfo;
    showWarning: boolean;
    isLoaderVisible: boolean;
    setSectors: React.Dispatch<React.SetStateAction<ChipEntity[]>>;
    setProducts: React.Dispatch<React.SetStateAction<ChipEntity[]>>;
    onChangeLocation: (data: ChipEntity[]) => void;
    onChangePeersChip: (chips: ChipEntity[]) => void;
    onEndTypingCallback: (value: string, type: "sectors" | "products") => void;
    onChangeProductsOrSectors: (newValue: ChipEntity[], type: "sectors" | "products") => void;
    handleInitialDataFetch: (type: "sectors" | "products") => void;
} => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    // Url params
    const { companyId } = useParams();
    const projectId = searchParams.get("projectId");
    const insightId = searchParams.get("insightId");

    // Redirect to 404 if invalid project id or not enough permission
    useRedirectOnProjectError([companyId, projectId, insightId]);

    // Data from store
    const isLoaderVisible = useAppSelector(getLoaderState);

    // Insight Data
    const { data: insight } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);

    // Domain Data
    const domainInfo = useAppSelector(getDomain);
    const { data: company } = useAppSelector(getProjectCompanyData);

    // Sectors
    const domainSectors = domainInfo.filters.selectedSectors;
    const [sectors, setSectors] = useState<ChipEntity[]>(domainSectors);

    // Products
    const domainProducts = domainInfo.filters.selectedProducts;
    const [products, setProducts] = useState<ChipEntity[]>(domainProducts);

    // Country
    const companyCountryId = company?.locations?.at(0)?.country.id;
    const domainCountry = domainInfo.filters.selectedCountry;
    const [country, setCountry] = useState(domainCountry || 0);

    // Peers
    const domainPeers = domainInfo.peers.data;
    const [peers, setPeers] = useState<Peer[]>(domainPeers);

    // Other Filter states
    const [locations, setLocations] = useState<ChipEntity[]>([
        { ...locationList[0], key: companyCountryId || 0, selected: Boolean(companyCountryId) },
        { ...locationList[1], selected: !Boolean(companyCountryId) },
    ]);

    const [autoCompleteProducts, setAutoCompleteProducts] = useState<TAutocompleteProducts>({
        status: "pristine",
        data: domainInfo.products.data,
    });

    // Flags
    const domain = company?.domains?.at(0)?.domain;

    const listOfSectors = domainInfo.sectors.list;
    const categorizedSectors = domainInfo.sectors.categorized;
    const sectorsRequestStatus = domainInfo.sectors.statusList;

    const [showWarning, setShowWarning] = useState(false);
    const [peersRequest, setPeersRequest] = useState<RequestInfo>("pristine");

    const validateFilters = (sectors: ChipEntity[], products: ChipEntity[]) => {
        const noMinSelectedSectors = sectors.every((sector) => sector.selected === false);
        const noMinSelectedProducts = products.filter((product) => product.selected).length < 3;
        setShowWarning(noMinSelectedSectors || noMinSelectedProducts);
    };

    const onEndTypingCallback = async (value: string, type: "sectors" | "products") => {
        if (!value.length) return;

        if (type === "products") {
            try {
                setAutoCompleteProducts({ ...autoCompleteProducts, status: "fetching" });
                const { data } = await fetchListOfProductsByValue(value);
                const results = data.results.map((product) => ({ key: product.id, value: product.name }));
                setAutoCompleteProducts({ data: results, status: "done" });
            } catch (error) {
                setAutoCompleteProducts({ ...autoCompleteProducts, status: "error" });
                console.error(error);
            }
        }
    };

    const onChangePeersChip = (chips: ChipEntity[]) => {
        const currentPeers = peers.slice(0, PEERS_LIMIT);
        const peerRemoved = differenceBy(currentPeers, chips, "key");

        if (peerRemoved.length === 1) {
            setPeers((currentPeers) => currentPeers.filter((currentPeer) => currentPeer.key !== peerRemoved[0].key));
        }
    };

    const onChangeLocation = (data: ChipEntity[]) => {
        const selectedChipKey = data.find((location) => location.selected)?.key as number;
        const selectedLocation = selectedChipKey === 1 ? 0 : selectedChipKey;
        setCountry(selectedLocation);
        handleFetchPeers({ sectors, products, country: selectedLocation });
    };

    const onChangeProductsOrSectors = (newValue: ChipEntity[], type: "sectors" | "products") => {
        const currentSectors = type === "sectors" ? newValue : sectors;
        const currentProducts = type === "products" ? newValue : products;

        validateFilters(currentSectors, currentProducts);
        handleFetchPeers({ sectors: currentSectors, products: currentProducts, country });
    };

    const handleInitialDataFetch = async (type: "sectors" | "products") => {
        if (type === "sectors") {
            if (!domainInfo.sectors.list.length && domainInfo.sectors.statusList === "pristine") {
                dispatch(fetchSectorsThunk());
            }
            if (!domainInfo.sectors.categorized.length && domainInfo.sectors.statusCategorized === "pristine") {
                dispatch(fetchCategorizedSectorsThunk());
            }
        }

        if (type === "products") {
            if (!domainInfo.products.data.length && domainInfo.products.status === "pristine") {
                setAutoCompleteProducts({ ...autoCompleteProducts, status: "fetching" });
                const results = await dispatch(fetchProductsThunk());
                const products = unwrapResult(results);
                setAutoCompleteProducts({ data: products, status: "done" });
            }
        }
    };

    const handleFetchPeers = useCallback(
        async (params: { sectors: ChipEntity[]; products: ChipEntity[]; country: number | null }) => {
            setPeersRequest("fetching");

            const { sectors, products, country } = params;
            const selSectors = sectors.filter(getSelectedElements).map(getElementKey);
            const selProducts = products.filter(getSelectedElements).map(getElementKey);

            const requestPayload = {
                columns: columnsDomainPeerOS,
                meta: { page_no: 1, page_size: PEERS_LIMIT },
                ...(selSectors.length > 0 && { sectors: selSectors }),
                ...(selProducts.length > 0 && { products: selProducts }),
                ...(!isNil(country) && { location: { countryId: country } }),
                ...(selSectors.length || selProducts.length
                    ? {
                          order_by: [
                              {
                                  operator: "SCORE_SECTORS_PRODUCTS_HITS",
                                  operand: {
                                      sector_ids: selSectors,
                                      product_ids: selProducts,
                                  },
                                  direction: "desc",
                              },
                              { field: "id", direction: "asc" },
                          ],
                      }
                    : {}),
            };

            const peersResults = unwrapResult(
                await dispatch(
                    fetchPeers({
                        requestPayload,
                        referenceCompany: Number(companyId),
                    })
                )
            );

            setPeers(peersResults);
            setPeersRequest("done");
        },
        [companyId, dispatch]
    );

    // Initial Fetch
    useEffect(() => {
        const initialFetch = async () => {
            const promises = [];
            let result = {} as TInitialFetch | TInitialFetchError;

            if (isEmpty(company) && companyId) {
                promises.push(dispatch(fetchProjectCompany(companyId)));
            }

            if (isEmpty(insight) && insightId && projectId) {
                promises.push(
                    dispatch(
                        getProjectInsight({
                            projectId,
                            path: `/project_${projectId}/insight_list/insight_${insightId}`,
                        })
                    )
                );
            }

            if (promises.length) {
                dispatch(toggleLoader(true));
                const response = await Promise.allSettled(promises);
                result = getInitialFetchResponse(response);

                if ("error" in result) {
                    console.error(result.reason);
                    dispatch(toggleLoader(false));
                    navigate(urlPaths.NotFound);
                    return;
                }
            }

            const companyParam = "company" in result ? result.company : company;
            const insightParam = "insight" in result ? result.insight : insight;

            if (!domainInfo.copiedDataToStore) {
                await setData(companyParam, insightParam);
            } else {
                validateFilters(sectors, products);
            }

            dispatch(toggleLoader(false));
        };

        const setData = async (
            companyData: ReturnType<typeof flattenSectorProductsDomain> | undefined,
            insightData: InsightData | undefined
        ) => {
            if (companyData === undefined || insightData === undefined) return;

            const filters = getInsightFilters(insightData);
            const relatedPeersPath = getInsightPeersPath(insightData)?.ref?.path;

            // Sectors
            const insightsSectors = getFiltersElements(filters, "sectors")?.reverse();
            const companySectors = companyData?.sectors.map(mapForChipEntity);
            const initSectors = (relatedPeersPath && insightsSectors?.length ? insightsSectors : companySectors) || [];
            dispatch(setSelectedSectors(initSectors));
            setSectors(initSectors);

            // Products
            const insightsProducts = getFiltersElements(filters, "products")?.reverse();
            const companyProducts = companyData?.products.map(mapForChipEntity);
            const initProducts =
                (relatedPeersPath && insightsProducts?.length ? insightsProducts : companyProducts) || [];
            dispatch(setSelectedProducts(initProducts));
            setProducts(initProducts);

            // Country
            const insightCountry = filters?.data?.geo_boundary.country_id;
            const initCountry = (relatedPeersPath ? insightCountry : companyData?.locations?.[0]?.country_id) || 0;
            dispatch(setSelectedCountry(initCountry));
            setLocations((currentLocations) => [
                { ...currentLocations[0], key: initCountry, selected: Boolean(companyCountryId) },
                { ...currentLocations[1], selected: !Boolean(companyCountryId) },
            ]);

            validateFilters(initSectors, initProducts);

            // Get peers Description and Location
            if (relatedPeersPath && projectId) {
                if (peersRequest !== "fetching" && peersRequest !== "done") {
                    setPeersRequest("fetching");
                    const companySet: ProjectCompanySet = unwrapResult(
                        await dispatch(fetchCompanySet({ projectId, path: relatedPeersPath }))
                    );

                    if (companySet.data.company_ids.length) {
                        const peersWithData = await getInitialPeersData(companySet);
                        dispatch(setSelectedPeers(peersWithData));
                        setPeers(peersWithData);
                    }
                    setPeersRequest("done");
                }
            } else {
                handleFetchPeers({
                    sectors: initSectors,
                    products: initProducts,
                    country: initCountry,
                });
            }

            dispatch(setDataToStore(true));
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        sectors,
        products,
        company,
        peers,
        peersRequest,
        domain,
        country,
        companyCountryId,
        locations,
        autoCompleteProducts,
        listOfSectors,
        categorizedSectors,
        sectorsRequestStatus,
        showWarning,
        isLoaderVisible,
        setSectors,
        setProducts,
        onChangeLocation,
        onChangePeersChip,
        onEndTypingCallback,
        onChangeProductsOrSectors,
        handleInitialDataFetch,
    };
};

export default useDomainLayout;
